import { getFramework, type IFramework } from '@/framework';
import { getLocaleStore } from '@/i18n';
import { type IMenuItemProps, type IRenderContextMenuItemArgs } from '@/plugins/context-menu';
import { type TextEditorPlugin } from '@/plugins/text-editor';
import { type Layer } from '../..';

/**
 * 翻转当前文字宿主前结束同层的活跃编辑会话。
 *
 * @param framework - 当前编辑器 framework。
 * @param layerId - 即将翻转的图层 ID。
 * @returns 无需退出或匹配会话已成功退出时返回 true。
 */
async function tryExitTextEditingIfNeeded(framework: IFramework, layerId: string): Promise<boolean> {
    const textEditor = framework.service.registration.getInstance<TextEditorPlugin>('TextEditorPlugin');
    if (textEditor?.getSession()?.editingLayer.id !== layerId) return true;
    await textEditor.exit();
    return textEditor.getSession()?.editingLayer.id !== layerId;
}

/**
 * 创建图层水平/垂直翻转菜单，并在需要时先安全退出文字编辑。
 *
 * @param args - 当前上下文菜单的激活与拾取图层。
 * @param identity - 当前编辑器实例标识。
 * @returns 可用的翻转菜单；Edgeless 模式或无图层时返回 null。
 */
export const layerFlipItem = (args: IRenderContextMenuItemArgs, identity: string) => {
    const { activeLayer, pickLayer } = args;
    const { $t } = getLocaleStore();
    const framework = getFramework(identity);
    const layer = pickLayer || activeLayer;
    const disabled = !layer;
    if (framework.service.edgelessCanvas.isEdgelessMode) return null;

    if (disabled) return null;
    const disableFlip = framework.service.behavior.get(layer)?.checkDisableFlip?.(layer);

    const item: IMenuItemProps = {
        disabled,
        name: $t((v) => v.comToolBar.flipBtn.layerRotate),
        type: 'flip',
        childMenus: [
            {
                name: $t((v) => v.comToolBar.flipBtn.horizontalRotate),
                disabled: disableFlip,
                type: 'flipH',
                icon: 'FlipHorizontalBold',
                onClick: () => {
                    void tryExitTextEditingIfNeeded(framework, layer.id).then((canFlip) => {
                        if (!canFlip) return;
                        const layerApi = framework.service.registration.getInstance<Layer>('Layer');
                        if (!layerApi) {
                            throw new Error('Layer is not found');
                        }
                        layerApi.flipX(layer.id);
                    });
                },
            },
            {
                name: $t((v) => v.comToolBar.flipBtn.verticalRotate),
                disabled: disableFlip,
                type: 'flipV',
                icon: 'FlipVerticalBold',
                onClick: () => {
                    void tryExitTextEditingIfNeeded(framework, layer.id).then((canFlip) => {
                        if (!canFlip) return;
                        const layerApi = framework.service.registration.getInstance<Layer>('Layer');
                        if (!layerApi) {
                            throw new Error('Layer is not found');
                        }
                        layerApi.flipY(layer.id);
                    });
                },
            },
        ],
    };

    return item;
};
